import { Button, Group, Stack, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useOrderConfiguration } from "@jasper/use-cases";
import {
  getWaxUpTypeSelection,
  WaxUpTypeEnum,
  type WaxUpTypeSelection,
} from "@jasper/client-models";

import type { ProductStepProps } from "./types";
import { buildWaxUpItems, WAX_UP_TYPE_SELECTIONS } from "./waxUpTypeSelection";
import { useRegisterStepSave } from "./useRegisterStepSave";

export const WaxUpTypeStep = ({
  stepKey,
  product,
  orderId,
  registerStepSave,
}: ProductStepProps) => {
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.waxUpType",
  });
  const { updateProduct, isUpdatingProduct } = useOrderConfiguration({
    orderId,
  });

  const defaultValues = useMemo(
    () => ({
      waxUpType:
        getWaxUpTypeSelection(product.items) ?? WaxUpTypeEnum.NUMERICAL,
    }),
    [product.items]
  );

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: async ({ value }) => {
      try {
        await updateProduct({
          productId: product.id,
          items: buildWaxUpItems({
            teeth: product.teeth,
            waxUpType: value.waxUpType,
          }),
        });
      } catch {
        notifications.show({
          title: t("errorNotificationTitle", { keyPrefix: "common" }),
          message: t("errorNotificationMessage", { keyPrefix: "common" }),
          color: "red",
        });
        return;
      }
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  return (
    <Stack h="100%" mih={0} p="md" gap="md">
      <Stack gap="md">
        <Title order={2} c="primary">
          {t("title")}
        </Title>
        <form.Field name="waxUpType">
          {field => (
            <Group gap="sm">
              {WAX_UP_TYPE_SELECTIONS.map(waxUpType => (
                <Button
                  key={waxUpType}
                  variant={
                    field.state.value === waxUpType ? "filled" : "outline"
                  }
                  onBlur={() => field.handleBlur()}
                  onClick={() =>
                    field.handleChange(waxUpType as WaxUpTypeSelection)
                  }
                  loading={isUpdatingProduct && field.state.value === waxUpType}
                >
                  {t(waxUpType)}
                </Button>
              ))}
            </Group>
          )}
        </form.Field>
      </Stack>
    </Stack>
  );
};
